import { Column } from '@/Atomic/Templates/Table'
import { CellRendererParams, TableColumn } from '@/Components/EntityTable/types'
import { ActivityCompaniesCell } from '@/Components/TableCells/ActivityCompaniesCell'
import { ActivityOverviewCell } from '@/Components/TableCells/ActivityOverviewCell'
import { ActivityPersonsCell } from '@/Components/TableCells/ActivityPersonsCell'
import { ActivityTimelineCell } from '@/Components/TableCells/ActivityTimelineCell'
import { AmountWithCurrencyCell } from '@/Components/TableCells/AmountWithCurrencyCell'
import { ListItemsCountCell } from '@/Components/TableCells/ListItemsCountCell'
import { ListObjectCell } from '@/Components/TableCells/ListObjectCell'
import { ShortDateCell } from '@/Components/TableCells/ShortDateCell'
import { SingleContactReferenceCell } from '@/Components/TableCells/SingleContactReferenceCell'
import { DataClassName, FeatureFlag, FeatureFlags } from '@/Data/const'
import { getEntityIconElementByEntity } from '@/utils/dataClassMap'

export const getTableCols = (
  dataClassName: string,
  isFeatureFlagEnabled: (flag: FeatureFlag) => boolean,
): TableColumn[] => {
  switch (dataClassName) {
    case DataClassName.ACTIVITY:
      return ACTIVITIES_TABLE_COLS
    case DataClassName.COMPANY:
      return isFeatureFlagEnabled(FeatureFlags.HIDE_REGION)
        ? COMPANIES_TABLE_COLS.filter((col) => col.accessor !== 'region')
        : COMPANIES_TABLE_COLS
    case DataClassName.DOCUMENT:
      return DOCUMENTS_TABLE_COLS
    case DataClassName.LEAD:
      return LEADS_TABLE_COLS
    case DataClassName.LIST:
      return LISTS_TABLE_COLS
    case DataClassName.OPPORTUNITY:
      return OPPORTUNITIES_TABLE_COLS
    case DataClassName.ORDER:
      return ORDERS_TABLE_COLS
    case DataClassName.PERSON:
      return PERSONS_TABLE_COLS
    case DataClassName.QUOTE:
      return QUOTES_TABLE_COLS
    case DataClassName.TICKET:
      return TICKETS_TABLE_COLS
    default:
      console.warn(`No table columns found for ${dataClassName}`)
      return []
  }
}

const ACTIVITIES_TABLE_COLS = [
  {
    accessor: 'subject',
    title: 'Activity',
    titleKey: 'activity',
    main: true,
    variant: Column.MAIN,
    noWrap: true,
    sortable: true,
    cell: ActivityOverviewCell,
  },
  {
    accessor: 'status',
    title: 'Status',
    variant: Column.WIDTH_160PX,
    noWrap: true,
  },
  {
    accessor: 'priority',
    title: 'Priority',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'beginAt',
    title: 'Timeline',
    variant: Column.WIDTH_160PX,
    titleKey: 'timeline',
    cell: ActivityTimelineCell,
    sortable: true,
  },
  {
    accessor: 'companies',
    title: 'Companies',
    variant: Column.FLEX,
    titleKey: 'companies',
    cell: ActivityCompaniesCell,
  },
  {
    accessor: 'persons',
    title: 'Persons',
    variant: Column.FLEX,
    titleKey: 'persons',
    cell: ActivityPersonsCell,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated At',
    variant: Column.WIDTH_120PX,
    sortable: true,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const COMPANIES_TABLE_COLS = [
  {
    accessor: 'image',
    title: '',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'name',
    title: 'Name',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'labels',
    title: 'Labels',
    variant: Column.WIDTH_80PX,
    sortable: true,
  },
  {
    accessor: 'group',
    title: 'Group',
    variant: Column.WIDTH_160PX,
  },
  {
    accessor: 'region',
    title: 'Region',
    variant: Column.WIDTH_260PX,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated On',
    titleKey: 'updatedOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const DOCUMENTS_TABLE_COLS = [
  {
    accessor: 'format',
    title: 'Format',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'displayTitle',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'status',
    title: 'Status',
    variant: Column.WIDTH_160PX,
    noWrap: true,
  },
  {
    accessor: 'version',
    title: 'Version',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'type',
    title: 'Type',
    variant: Column.WIDTH_260PX,
  },
  {
    accessor: 'size',
    title: 'Size',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const getIcon = ({ dataItem }: CellRendererParams) => {
  return getEntityIconElementByEntity(dataItem, { avatarSize: 'lg' })
}

const LEADS_TABLE_COLS = [
  {
    accessor: 'icon',
    title: '',
    variant: Column.WIDTH_80PX,
    cell: getIcon,
  },
  {
    accessor: 'name',
    title: 'Name',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'createdAt',
    title: 'Created At',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: 'email',
    title: 'Email',
    variant: Column.WIDTH_260PX,
    sortable: true,
  },
  {
    accessor: 'occasion',
    title: 'Occasion',
    variant: Column.WIDTH_160PX,
    sortable: true,
  },
  {
    accessor: 'labels',
    title: 'Labels',
    variant: Column.WIDTH_80PX,
    sortable: true,
  },
  {
    accessor: 'status',
    title: 'Status',
    variant: Column.WIDTH_260PX,
    noWrap: true,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated On',
    titleKey: 'updatedOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const LISTS_TABLE_COLS = [
  {
    accessor: 'title',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'status',
    title: 'Status',
    variant: Column.WIDTH_140PX,
    sortable: true,
    noWrap: true,
  },
  {
    accessor: 'entityType',
    title: 'Object',
    variant: Column.WIDTH_120PX,
    titleKey: 'object',
    cell: ListObjectCell,
    noWrap: true,
    sortable: true,
  },
  {
    accessor: 'type',
    title: 'Type',
    variant: Column.WIDTH_120PX,
    sortable: true,
  },
  {
    accessor: 'itemsCount',
    title: 'Count',
    variant: Column.WIDTH_90PX,
    titleKey: 'count',
    cell: ListItemsCountCell,
  },
  {
    accessor: 'updatedAt',
    title: 'Last updated',
    titleKey: 'lastUpdated',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: 'createdAt',
    title: 'Created At',
    variant: Column.WIDTH_120PX,
    sortable: true,
  },
  {
    accessor: 'createdBy',
    title: 'Created By',
    variant: Column.WIDTH_160PX,
    cell: SingleContactReferenceCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const OPPORTUNITIES_TABLE_COLS = [
  {
    accessor: 'type',
    title: 'Type',
    variant: Column.WIDTH_160PX,
  },
  {
    accessor: 'keyword',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'totalPrice',
    title: 'Price',
    variant: Column.WIDTH_120PX,
    noWrap: true,
    sortable: true,
    cell: AmountWithCurrencyCell,
  },
  {
    accessor: 'customer',
    title: 'Company',
    variant: Column.WIDTH_160PX,
    cell: SingleContactReferenceCell,
  },
  {
    accessor: 'opportunityAt',
    title: 'Created On',
    titleKey: 'createdOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: 'status',
    title: 'State',
    variant: Column.WIDTH_260PX,
    noWrap: true,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated On',
    titleKey: 'updatedOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const ORDERS_TABLE_COLS = [
  {
    accessor: 'type',
    title: 'Type',
    variant: Column.WIDTH_160PX,
  },
  {
    accessor: 'keyword',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'totalPrice',
    title: 'Price',
    variant: Column.WIDTH_120PX,
    noWrap: true,
    sortable: true,
    cell: AmountWithCurrencyCell,
  },
  {
    accessor: 'customer',
    title: 'Company',
    variant: Column.WIDTH_120PX,
    cell: SingleContactReferenceCell,
  },
  {
    accessor: 'orderAt',
    title: 'Created On',
    titleKey: 'createdOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: 'status',
    title: 'State',
    variant: Column.WIDTH_260PX,
    noWrap: true,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated On',
    titleKey: 'updatedOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const QUOTES_TABLE_COLS = [
  {
    accessor: 'type',
    title: 'Type',
    variant: Column.WIDTH_160PX,
  },
  {
    accessor: 'keyword',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'totalPrice',
    title: 'Price',
    variant: Column.WIDTH_160PX,
    noWrap: true,
    sortable: true,
    cell: AmountWithCurrencyCell,
  },
  {
    accessor: 'customer',
    title: 'Company',
    variant: Column.WIDTH_160PX,
    cell: SingleContactReferenceCell,
  },
  {
    accessor: 'quoteAt',
    title: 'Created On',
    titleKey: 'createdOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: 'status',
    title: 'State',
    variant: Column.WIDTH_260PX,
    noWrap: true,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated On',
    titleKey: 'updatedOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const PERSONS_TABLE_COLS = [
  {
    accessor: 'image',
    title: '',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'name',
    title: 'Name',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'parent',
    title: 'Company',
    variant: Column.WIDTH_260PX,
  },
  {
    accessor: 'email',
    title: 'Email',
    variant: Column.WIDTH_260PX,
    sortable: true,
  },
  {
    accessor: 'phone',
    title: 'Phone Number',
    variant: Column.WIDTH_160PX,
  },
  {
    accessor: 'labels',
    title: 'Labels',
    variant: Column.WIDTH_80PX,
    sortable: true,
  },
  {
    accessor: 'group',
    title: 'Group',
    variant: Column.WIDTH_160PX,
    sortable: true,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated On',
    titleKey: 'updatedOn',
    variant: Column.WIDTH_120PX,
    sortable: true,
    cell: ShortDateCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]

const TICKETS_TABLE_COLS = [
  {
    accessor: 'number',
    title: 'Number',
    variant: Column.WIDTH_160PX,
  },
  {
    accessor: 'keyword',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
    sortable: true,
  },
  {
    accessor: 'customer',
    title: 'Customer',
    variant: Column.WIDTH_260PX,
    cell: SingleContactReferenceCell,
  },
  {
    accessor: 'origin',
    title: 'Origin',
    variant: Column.WIDTH_80PX,
    sortable: true,
  },
  {
    accessor: 'status',
    title: 'Status',
    variant: Column.WIDTH_260PX,
    noWrap: true,
  },
  {
    accessor: 'updatedAt',
    title: 'Updated At',
    variant: Column.WIDTH_160PX,
    sortable: true,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]
